import React, { useState } from 'react';
import {
  Box, Tabs, Tab, Card, CardContent, Typography, Button, Stack, Alert, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, Chip,
} from '@mui/material';
import { AutoAwesome, FileUpload } from '@mui/icons-material';
import api from '../services/api';
import TextField from '../components/common/TextField';

const fmt = (v: number) => `$${(v ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const AiPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <AutoAwesome color="primary" />
        <Typography variant="h4" fontWeight={700}>Asistente IA</Typography>
        <Chip size="small" color="secondary" label="Plan IA" />
      </Stack>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Escaneo de facturas" />
        <Tab label="Productos sin venta" />
        <Tab label="Precios sugeridos" />
      </Tabs>
      {tab === 0 && <InvoiceScanTab />}
      {tab === 1 && <DeadStockTab />}
      {tab === 2 && <PricingTab />}
    </Box>
  );
};

const InvoiceScanTab: React.FC = () => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any | null>(null);
  const [suggested, setSuggested] = useState<any[] | null>(null);

  const onFile = async (file: File) => {
    setBusy(true); setError(null); setResult(null); setSuggested(null);
    try {
      const res = await api.aiScanInvoice(file);
      setResult(res.invoice);
      setSuggested(res.suggestedProducts || null);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo procesar la factura.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Subí una foto o PDF de la factura del proveedor y la IA extrae proveedor, items, cantidades y costos.
        </Typography>
        <Button component="label" variant="contained" startIcon={<FileUpload />} disabled={busy}>
          {busy ? 'Procesando…' : 'Subir factura'}
          <input hidden type="file" accept="image/*,application/pdf" onChange={e => e.target.files?.[0] && onFile(e.target.files[0])} />
        </Button>
        {busy && <CircularProgress size={22} sx={{ ml: 2 }} />}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {result && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {result.supplierName || 'Proveedor sin nombre'} {result.invoiceNumber ? `· ${result.invoiceNumber}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">{result.date || ''}</Typography>
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Descripción</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell align="right">Cant.</TableCell>
                  <TableCell align="right">Costo unit.</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(result.items || []).map((it: any, idx: number) => (
                  <TableRow key={idx}>
                    <TableCell>{it.description}</TableCell>
                    <TableCell>{it.sku || '—'}</TableCell>
                    <TableCell align="right">{it.quantity}</TableCell>
                    <TableCell align="right">{fmt(it.unitCost)}</TableCell>
                    <TableCell align="right">{fmt(it.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {result.total != null && (
              <Typography sx={{ mt: 1, textAlign: 'right' }}>Total factura: <b>{fmt(result.total)}</b></Typography>
            )}
            {suggested && suggested.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {suggested.length} producto(s) de la factura no existen en tu catálogo: {suggested.map((s: any) => s.description).join(', ')}
              </Alert>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const DeadStockTab: React.FC = () => {
  const [days, setDays] = useState(60);
  const [items, setItems] = useState<any[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setBusy(true); setError(null);
    try { const res = await api.aiDeadStock(days); setItems(res.items || []); }
    catch (e: any) { setError(e?.response?.data?.message || 'No se pudo cargar.'); }
    finally { setBusy(false); }
  };

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <TextField size="small" type="number" label="Días sin venta" value={days} onChange={e => setDays(Number(e.target.value))} sx={{ width: 160 }} />
          <Button variant="contained" onClick={load} disabled={busy}>{busy ? 'Cargando…' : 'Analizar'}</Button>
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        {items && (
          items.length === 0 ? <Typography color="text.secondary">No hay productos sin venta en el período. 🎉</Typography> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Producto</TableCell><TableCell>SKU</TableCell>
                  <TableCell align="right">Stock</TableCell><TableCell align="right">Valor stock</TableCell>
                  <TableCell>Última venta</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((p: any) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>{p.sku}</TableCell>
                    <TableCell align="right">{p.currentStock}</TableCell>
                    <TableCell align="right">{fmt(p.stockValue)}</TableCell>
                    <TableCell>{p.lastSale ? new Date(p.lastSale).toLocaleDateString('es-AR') : 'Nunca'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        )}
      </CardContent>
    </Card>
  );
};

const PricingTab: React.FC = () => {
  const [margin, setMargin] = useState(40);
  const [items, setItems] = useState<any[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setBusy(true); setError(null);
    try { const res = await api.aiPricingSuggestions(margin); setItems(res.items || []); }
    catch (e: any) { setError(e?.response?.data?.message || 'No se pudo cargar.'); }
    finally { setBusy(false); }
  };

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <TextField size="small" type="number" label="Margen objetivo (%)" value={margin} onChange={e => setMargin(Number(e.target.value))} sx={{ width: 180 }} />
          <Button variant="contained" onClick={load} disabled={busy}>{busy ? 'Cargando…' : 'Sugerir precios'}</Button>
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        {items && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Producto</TableCell><TableCell align="right">Costo</TableCell>
                <TableCell align="right">Precio actual</TableCell><TableCell align="right">Margen actual</TableCell>
                <TableCell align="right">Precio sugerido</TableCell><TableCell align="right">Δ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((p: any) => (
                <TableRow key={p.id}>
                  <TableCell>{p.name}</TableCell>
                  <TableCell align="right">{fmt(p.cost)}</TableCell>
                  <TableCell align="right">{fmt(p.currentPrice)}</TableCell>
                  <TableCell align="right">{p.currentMarginPct}%</TableCell>
                  <TableCell align="right"><b>{fmt(p.suggestedPrice)}</b></TableCell>
                  <TableCell align="right" sx={{ color: p.delta >= 0 ? 'success.main' : 'error.main' }}>
                    {p.delta >= 0 ? '+' : ''}{fmt(p.delta)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default AiPage;
